import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import LicenseCard from '../../components/cultivar/LicenseCard';
import PendingLicenseBanner from '../../components/cultivar/PendingLicenseBanner';
import {
  Scale, Building2, Handshake, ChevronLeft, ChevronRight,
  Loader2, Filter, X
} from 'lucide-react';
import type { RegionalLicense, Pagination } from '../../types/cultivar';
import cultivarService from '../../services/cultivar';
import { useAuth } from '../../context/AuthContext';

type ViewMode = 'licensor' | 'licensee' | 'all';

function LicenseManagement() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [licenses, setLicenses] = useState<RegionalLicense[]>([]);
  const [pendingConfirmations, setPendingConfirmations] = useState<RegionalLicense[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const viewMode = (searchParams.get('view') as ViewMode) || 'all';
  const statusFilter = searchParams.get('status') || '';
  const currentPage = Number(searchParams.get('page')) || 1;
  const cultivarIdFilter = searchParams.get('cultivar_id');

  const loadLicenses = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [licensesRes, pendingRes] = await Promise.all([
        cultivarService.getMyLicenses({
          role: viewMode !== 'all' ? viewMode : undefined,
          status: statusFilter || undefined,
          cultivar_id: cultivarIdFilter ? Number(cultivarIdFilter) : undefined,
          page: currentPage,
          limit: 12,
        }),
        cultivarService.getPendingConfirmations(),
      ]);

      if (licensesRes.success && licensesRes.data) {
        setLicenses(licensesRes.data.licenses);
        setPagination(licensesRes.data.pagination);
      } else {
        setError(licensesRes.message || 'Failed to load licenses');
      }

      if (pendingRes.success && pendingRes.data) {
        setPendingConfirmations(pendingRes.data);
      }
    } catch (err) {
      console.error('Error loading licenses:', err);
      setError('Failed to load licenses. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [viewMode, statusFilter, currentPage, cultivarIdFilter]);

  useEffect(() => {
    loadLicenses();
  }, [loadLicenses]);

  const setViewMode = (mode: ViewMode) => {
    const params = new URLSearchParams(searchParams);
    params.set('view', mode);
    params.delete('page');
    setSearchParams(params, { replace: true });
  };

  const setStatusFilter = (status: string) => {
    const params = new URLSearchParams(searchParams);
    if (status) {
      params.set('status', status);
    } else {
      params.delete('status');
    }
    params.delete('page');
    setSearchParams(params, { replace: true });
  };

  const setPage = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(page));
    setSearchParams(params, { replace: true });
  };

  const getLicenseUserRole = (license: RegionalLicense): 'licensor' | 'licensee' | 'observer' => {
    if (license.licensor_org_id === user?.organisation_id) return 'licensor';
    if (license.licensee_org_id === user?.organisation_id) return 'licensee';
    return 'observer';
  };

  const viewModes: { key: ViewMode; label: string; icon: typeof Scale }[] = [
    { key: 'all', label: 'All Licenses', icon: Scale },
    { key: 'licensor', label: 'As Licensor', icon: Building2 },
    { key: 'licensee', label: 'As Licensee', icon: Handshake },
  ];

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'active', label: 'Active' },
    { value: 'pending_confirmation', label: 'Awaiting Signature' },
    { value: 'suspended', label: 'Suspended' },
    { value: 'terminated', label: 'Terminated' },
    { value: 'rejected', label: 'Declined' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="License Management"
        subtitle="Manage your cultivar licenses and entitlements"
        icon={<Scale className="h-6 w-6 text-white" />}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Awaiting-signature banner — read-only nudge into the agreement view. */}
        <PendingLicenseBanner pendingLicenses={pendingConfirmations} />

        {/* View Mode Tabs & Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-neutral-200 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4">
            <div className="flex items-center gap-1 bg-neutral-100 rounded-lg p-1">
              {viewModes.map((mode) => {
                const Icon = mode.icon;
                const isActive = viewMode === mode.key;
                return (
                  <button
                    key={mode.key}
                    onClick={() => setViewMode(mode.key)}
                    className={`
                      flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors
                      ${isActive
                        ? 'bg-white text-primary-600 shadow-sm'
                        : 'text-neutral-600 hover:text-neutral-900'
                      }
                    `}
                  >
                    <Icon className="h-4 w-4" />
                    {mode.label}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-3">
              <Filter className="h-4 w-4 text-neutral-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="input-professional py-2 text-sm"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {(statusFilter || cultivarIdFilter) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const params = new URLSearchParams();
                    params.set('view', viewMode);
                    setSearchParams(params, { replace: true });
                  }}
                >
                  <X className="h-4 w-4" />
                  Clear
                </Button>
              )}
            </div>
          </div>

          {cultivarIdFilter && (
            <div className="px-4 pb-4 pt-0">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-50 text-primary-700 rounded-full text-sm">
                Filtered by cultivar ID: {cultivarIdFilter}
                <button
                  onClick={() => {
                    const params = new URLSearchParams(searchParams);
                    params.delete('cultivar_id');
                    setSearchParams(params, { replace: true });
                  }}
                  className="hover:text-primary-900"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-10 w-10 text-primary-600 animate-spin" />
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-error-600 mb-4">{error}</p>
              <Button variant="outline" onClick={loadLicenses}>
                Try Again
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {!loading && !error && licenses.length === 0 && (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="h-16 w-16 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-4">
                <Scale className="h-8 w-8 text-neutral-400" />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                No Licenses Found
              </h3>
              <p className="text-neutral-600 mb-6 max-w-md mx-auto">
                {statusFilter
                  ? 'No licenses match your current filters.'
                  : viewMode === 'licensor'
                  ? 'You have not issued any licences yet. Open a cultivar from the registry to issue one.'
                  : viewMode === 'licensee'
                  ? 'You do not hold any licences yet. They will appear here once a licensor issues one to your organisation.'
                  : 'No licences yet. Licensors issue licences to nurseries and orchards from the cultivar registry.'}
              </p>
              <Button variant="primary" onClick={() => navigate('/cultivar')}>
                Open cultivar registry
              </Button>
            </CardContent>
          </Card>
        )}

        {/* License Grid */}
        {!loading && !error && licenses.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {licenses.map((license) => (
                <LicenseCard
                  key={license.id}
                  license={license}
                  userRole={getLicenseUserRole(license)}
                />
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setPage(currentPage - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>

                <span className="px-4 text-sm text-neutral-600">
                  Page {currentPage} of {pagination.pages}
                </span>

                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === pagination.pages}
                  onClick={() => setPage(currentPage + 1)}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default LicenseManagement;
